import React, { useContext, useEffect, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Toolbar,
  Typography,
  Collapse,
  ListItemButton,
} from '@mui/material';
import { Search, MoreVert, ExpandMore, ExpandLess } from '@mui/icons-material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate } from 'react-router-dom';
import FormsBottomNavbar from './../Post Module/FormsBottomNavbar';
import { AuthContext } from './../../AuthContext/AuthContext';
import axios from 'axios';
import { BASE_URL } from './../../Api/ApiUrls';

const InboxList = () => {
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [expandedProperty, setExpandedProperty] = useState(null);
  const [users, setUsers] = useState({});
  const [properties, setProperties] = useState({});
  const [propertyParticipants, setPropertyParticipants] = useState({});
  const [userHasProperties, setUserHasProperties] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch properties first to determine if user has properties
        const propertiesResponse = await axios.get(`${BASE_URL}/property/`);
        const propertiesData = propertiesResponse.data.reduce((acc, property) => {
          acc[property.property_id] = property;
          return acc;
        }, {});
        setProperties(propertiesData);

        // Check if user has any properties
        const hasProperties = propertiesResponse.data.some(property => property.user_id == userId);
        setUserHasProperties(hasProperties);

        // Fetch messages
        const messagesResponse = await axios.get(`${BASE_URL}/chat-messages/`);
        // Filter out messages without property_id
        const messagesData = messagesResponse.data.filter(msg => msg.property_id !== null);

        // Fetch users
        const usersResponse = await axios.get(`${BASE_URL}/users/`);
        const usersData = usersResponse.data.reduce((acc, user) => {
          acc[user.user_id] = user;
          return acc;
        }, {});
        setUsers(usersData);

        // Filter messages based on user's property ownership
        const userMessages = messagesData.filter(msg => {
          if (hasProperties) {
            // If user has properties, show messages where:
            // - They are the sender/receiver AND the property belongs to them
            // - OR they are the sender/receiver of any property message
            return (msg.user_id == userId || msg.receiver == userId) && 
                   (propertiesData[msg.property_id]?.user_id == userId || true);
          } else {
            // If user doesn't have properties, only show messages where they are involved
            return (msg.user_id == userId || msg.receiver == userId);
          }
        });

        // Group by property_id (latest message per property)
        const latestMessagesMap = new Map();
        const participantsMap = new Map();

        userMessages.forEach((msg) => {
          const key = msg.property_id;
          
          // For latest messages
          if (!latestMessagesMap.has(key)) {
            latestMessagesMap.set(key, msg);
          }
          
          // For participants
          if (!participantsMap.has(key)) {
            participantsMap.set(key, new Set());
          }
          const participants = participantsMap.get(key);
          if (msg.user_id != userId) participants.add(msg.user_id);
          if (msg.receiver != userId) participants.add(msg.receiver);
        });

        const latestMessages = Array.from(latestMessagesMap.values());
        setMessages(latestMessages);
        setFilteredMessages(latestMessages);
        
        // Convert participants Map to object with arrays
        const participantsObj = {};
        participantsMap.forEach((value, key) => {
          participantsObj[key] = Array.from(value);
        });
        setPropertyParticipants(participantsObj);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };

    fetchData();
  }, [userId]);

  useEffect(() => {
    const lowerSearch = searchText.toLowerCase();
    const filtered = messages.filter(
      (msg) =>
        msg.message.toLowerCase().includes(lowerSearch) ||
        (properties[msg.property_id]?.property_name || '').toLowerCase().includes(lowerSearch) ||
        msg.property_id?.toString().includes(lowerSearch)
    );
    setFilteredMessages(filtered);
  }, [searchText, messages, properties]);

  const handleExpandProperty = (propertyId) => {
    setExpandedProperty(expandedProperty === propertyId ? null : propertyId);
  };

  const handlePropertyClick = (propertyId, otherUserId) => {
    if (userHasProperties && properties[propertyId]?.user_id == userId) {
      // If user owns the property, expand/collapse the accordion
      handleExpandProperty(propertyId);
    } else {
      // If user doesn't own the property, navigate directly to chat
      navigate(`/chat/${propertyId}/${otherUserId}`);
    }
  };

  const getOtherUser = (message) => {
    const otherUserId = message.user_id == userId ? message.receiver : message.user_id;
    return users[otherUserId] || {};
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 800,
        mx: 'auto',
        minHeight: '100vh',
        bgcolor: 'rgb(239, 231, 221)',
        borderRadius: { xs: 0, sm: 3 },
        boxShadow: 3,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'black', color: 'white' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton edge="start" size="small" onClick={() => navigate(-1)} sx={{ color: 'white' }}>
            <ArrowBackIosNewIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Inbox ({filteredMessages.length})
          </Typography>
          <IconButton edge="end" size="small" sx={{ color: 'white' }}>
            <MoreVert />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Search */}
      <Paper
        sx={{
          m: 2,
          px: 2,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 5,
          backgroundColor: '#f0f0f0',
          border: '1px solid black',
        }}
      >
        <Search sx={{ color: 'gray', mr: 1 }} />
        <InputBase
          placeholder="Search"
          fullWidth
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Paper>

      {/* Message List */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: 1,
          pb: 7,
        }}
      >
        <List>
          {filteredMessages.length > 0 ? (
            filteredMessages.map((msg, index) => {
              const property = properties[msg.property_id] || {};
              const otherUser = getOtherUser(msg);
              const participants = propertyParticipants[msg.property_id] || [];
              const isPropertyOwner = property.user_id == userId;
              
              return (
                <React.Fragment key={index}>
                  <ListItemButton
                    alignItems="flex-start"
                    onClick={() => handlePropertyClick(msg.property_id, otherUser.user_id)}
                  >
                    <ListItemAvatar>
                      <Avatar src={otherUser.profile}>
                        {otherUser.first_name?.[0] || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography fontWeight={500} noWrap>
                            {property.property_name || `Property ${msg.property_id}`}
                            {isPropertyOwner && " (Your Property)"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(msg.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" noWrap>
                          {msg.message}
                        </Typography>
                      }
                    />
                    {userHasProperties && isPropertyOwner && (
                      expandedProperty === msg.property_id ? <ExpandLess /> : <ExpandMore />
                    )}
                  </ListItemButton>
                  
                  {userHasProperties && isPropertyOwner && (
                    <Collapse in={expandedProperty === msg.property_id} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {participants.map((participantId) => {
                          const user = users[participantId] || {};
                          return (
                            <ListItemButton 
                              key={participantId}
                              sx={{ pl: 4 }}
                              onClick={() => navigate(`/chat/${msg.property_id}/${participantId}`)}
                            >
                              <ListItemAvatar>
                                <Avatar src={user.profile}>
                                  {user.first_name?.[0] || 'U'}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={`${user.first_name || 'Unknown'} ${user.last_name || ''}`}
                                secondary={`Click to chat about ${property.property_name || 'this property'}`}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                  
                  {index !== filteredMessages.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <ListItem>
              <ListItemText 
                primary="No messages found"
                secondary="You don't have any conversations yet"
                sx={{ textAlign: 'center' }}
              />
            </ListItem>
          )}
        </List>
      </Box>

      {/* Bottom Navbar */}
      <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }}>
        <FormsBottomNavbar />
      </Box>
    </Box>
  );
};

export default InboxList;